import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { LogsTable } from '@/modules/admin/analytics/components/LogsTable';
import { StatsCards } from '@/modules/admin/analytics/components/StatsCards';
import { UsageChart } from '@/modules/admin/analytics/components/UsageChart';
import { useGetAnalyticsStats } from '@/modules/admin/hooks/use-admin';
import { useGetApiUsageLogs } from '@/graphql/queries/admin';
import { MoreVertical, RefreshCw, Settings } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data: stats, isLoading: loadingStats, refetch: refetchStats } = useGetAnalyticsStats();

  const {
    data: logsResult,
    isLoading: loadingLogs,
    refetch: refetchLogs,
  } = useGetApiUsageLogs({
    paging: { pageNo: page, pageSize },
  });

  const logs = (logsResult?.getApiUsageLogss?.items || []).map((item) => ({
    id: item.ItemId,
    templateSlug: item.TemplateSlug ?? null,
    endpoint: item.Endpoint ?? '',
    model: item.Model ?? '',
    totalTokens: item.TotalTokens ?? 0,
    cost: item.Cost ?? 0,
    responseTime: item.ResponseTime ?? 0,
    success: item.Success ?? false,
    createdAt: item.CreatedDate ?? '',
  }));
  const totalCount = logsResult?.getApiUsageLogss?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRefresh = () => {
    refetchStats();
    refetchLogs();
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-row items-start justify-between gap-3 md:gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--fg))] font-heading">
            {t('ADMIN_ANALYTICS_TITLE')}
          </h1>
          <p className="text-sm md:text-base text-globe-grey mt-1">
            {t('ADMIN_ANALYTICS_DESCRIPTION')}
          </p>
        </div>

        {/* Desktop: inline Refresh + Settings. */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <Button variant="primaryOutline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('ADMIN_ANALYTICS_ACTIONS_REFRESH')}
          </Button>
          <Link to="/admin/analytics/settings">
            <Button variant="primary" size="sm">
              <Settings className="mr-2 h-4 w-4" />
              {t('ADMIN_ANALYTICS_ACTIONS_SETTINGS')}
            </Button>
          </Link>
        </div>

        {/* Mobile: collapse both actions into a 3-dot menu. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="md:hidden h-9 w-9 shrink-0"
              aria-label="Analytics actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onSelect={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('ADMIN_ANALYTICS_ACTIONS_REFRESH')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/admin/analytics/settings')}>
              <Settings className="h-4 w-4 mr-2" />
              {t('ADMIN_ANALYTICS_ACTIONS_SETTINGS')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs defaultValue="overview" className="space-y-4 md:space-y-6">
        <TabsList>
          <TabsTrigger value="overview">{t('ADMIN_ANALYTICS_TABS_OVERVIEW')}</TabsTrigger>
          <TabsTrigger value="logs">{t('ADMIN_ANALYTICS_TABS_LOGS')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <StatsCards
            stats={
              stats?.summary || {
                totalRequests: 0,
                totalTokens: 0,
                totalCost: 0,
                avgResponseTime: 0,
                successRate: 0,
              }
            }
            loading={loadingStats}
          />

          <div className="grid gap-4 lg:grid-cols-7">
            <UsageChart data={stats?.dailyTrends || []} loading={loadingStats} />

            <div className="lg:col-span-3 space-y-4">
              <div className="rounded-xl border border-secondary-100 bg-card text-card-foreground shadow">
                <div className="p-4 md:p-6 flex flex-col gap-3 md:gap-4">
                  <h3 className="font-semibold leading-none tracking-tight">
                    {t('ADMIN_ANALYTICS_TOPTEMPLATES_TITLE')}
                  </h3>
                  <div className="space-y-3 md:space-y-4">
                    {loadingStats ? (
                      <div className="space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="h-8 bg-muted rounded animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      stats?.byTemplate?.slice(0, 5).map((item) => (
                        <div
                          key={item.templateSlug}
                          className="flex items-center justify-between gap-3"
                        >
                          <div className="space-y-1 min-w-0">
                            <p className="text-sm font-medium leading-none truncate">
                              {item.templateSlug}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t('ADMIN_ANALYTICS_TOPTEMPLATES_REQUESTS', {
                                count: item.requests,
                              })}
                            </p>
                          </div>
                          <div className="font-medium shrink-0">${item.cost.toFixed(4)}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <LogsTable
            logs={logs}
            pagination={{ page, limit: pageSize, total: totalCount, totalPages }}
            onPageChange={handlePageChange}
            loading={loadingLogs}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
